import React, { useContext } from "react";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import Moment from "moment";
import { Box, Button, Collapse, Grid, IconButton, Paper, TextField } from "@material-ui/core";
import { Autocomplete, createFilterOptions } from "@material-ui/lab";
import { Close } from "@material-ui/icons";
import { DatePicker } from "@material-ui/pickers";
import { MainContext } from "../../../../App";
import { PostulanteEditarContext } from "../index";
import { FormikInitialValues, FormikValidationSchema } from "./experiencia_utils";
import { uploadFileToCloudStorage } from "../../../../services/utilities/files";
import { ActualizacionRequest, ExperienciaRequest, PostulanteRequest } from "../../../../services/request/reclutamiento/postulantes";
import { Agregar } from "../../../../services/request/reclutamiento/empresas";

const filter = createFilterOptions();

export default function ExperienciaDialog(props) {
	const {
		is_open,
		set_is_open,
	} = props;

	const { postulante_id } = useParams();
	const { usuarioSesion } = useContext(MainContext);
	const { empresas, formik } = useContext(PostulanteEditarContext);
	const notistack = useSnackbar();

	const formikDialog = useFormik({
		initialValues: FormikInitialValues,
		validationSchema: FormikValidationSchema,
		onSubmit: async function (values, helper) {
			try {
				notistack.enqueueSnackbar("Agregando nueva experiencia del postulante...", {
					anchorOrigin: {
						horizontal: "center",
						vertical: "bottom"
					},
				});

				values.empresa = values.empresa_object.nombre;

				if (values.empresa_object.is_new) {
					//Si se agregó una empresa nueva.
					let nuevaEmpresa = {
						nombre: values.empresa_object.nombre,
						contacto: {
							sitio_web: "",
						},
					}
					//Se crea la nueva empresa.
					await Agregar(nuevaEmpresa);
				}

				if (values.is_trabajando_object) {
					//Si se seleccionó que aún está trabajando, se asigna.
					values.is_trabajando = values.is_trabajando_object.valor;
				}

				//Si existe un archivo de certificado.
				if (values.file) {
					notistack.enqueueSnackbar("Subiendo certificado a la nube...", {
						anchorOrigin: {
							horizontal: "center",
							vertical: "bottom"
						},
					});
					let ahora = Moment().format("DD_MM_YYYY_HH_mm_ss");
					const archivo = await uploadFileToCloudStorage(values.file, "certificados-experiencia-test", `experiencia_adjunto_${ahora}`);
					values.certificado = archivo;
				}

				//Se agregan experiencia y actualización del postulante.
				let responses = await Promise.all([
					ExperienciaRequest.Agregar(postulante_id, values),
					ActualizacionRequest.Agregar(postulante_id, usuarioSesion.ref, "Nueva experiencia"),
				]);

				//Nueva experiencia al postulante.
				let experiencia = responses[0];
				//Nuevo registro de actualización del postulante.
				let actualizacion = responses[1];

				//Se actualizan los datos del postulante.
				let experiencias = [...formik.values.experiencias, experiencia];
				formik.setFieldValue("experiencias", experiencias);
				formik.setFieldValue("actualizaciones", [actualizacion, ...formik.values.actualizaciones]);

				let fechasInicio = experiencias.filter(e => e.fecha_inicio)
					.map(e => Moment(e.fecha_inicio));
				//Si existen fechas de inicio.
				if (fechasInicio.length > 0) {
					let primerFecha = Moment.min(fechasInicio);
					await PostulanteRequest.Actualizar(postulante_id, { inicio_vida_laboral: primerFecha });
				}

				notistack.enqueueSnackbar("Experiencia del postulante agregada exitosamente.", {
					variant: "success",
					anchorOrigin: {
						horizontal: "center",
						vertical: "bottom"
					},
					action: (key) => <IconButton onClick={() => notistack.closeSnackbar()}><Close /></IconButton>
				});
			} catch (error) {
				console.error(error);
				notistack.enqueueSnackbar("Error al intentar agregar la experiencia del postulante.", {
					variant: "error",
					anchorOrigin: {
						horizontal: "center",
						vertical: "bottom"
					},
					action: (key) => <IconButton onClick={() => notistack.closeSnackbar()}><Close /></IconButton>
				});
			} finally {
				handleCloseDialog();
				helper.resetForm();
				empresas.mutate();
			}
		}
	});

	/**
	 * Método encargado de cerrar el popup.
	 */
	const handleCloseDialog = () => {
		set_is_open(false);
		formikDialog.resetForm();
	}

	/**
	 * Método encargado de determinar si el botón está habilitado o deshabilitado.
	 * @returns Estado del botón.
	 */
	const isDisabled = () => {
		let check = Boolean(
			formikDialog.isSubmitting ||
			!formikDialog.values.empresa_object ||
			!formikDialog.values.cargo ||
			!formikDialog.values.fecha_inicio ||
			Object.keys(formikDialog.errors).length > 0
		);
		return check;
	}

	return (
		<Collapse in={is_open}>
			<Paper style={{ padding: 15, borderRadius: 25 }}>
				<Grid container spacing={2}>
					{/* CAMPO EMPRESA */}
					<Grid item xs={6}>
						<Autocomplete
							value={formikDialog.values.empresa_object}
							onChange={(event, value) => {
								if (value) {
									if (typeof value === "string") {
										//Si es de tipo texto.
										formikDialog.setFieldValue("empresa_object", { nombre: value, is_new: false });
									}
									if (typeof value === "object") {
										//Si es de tipo objeto.
										formikDialog.setFieldValue("empresa_object", { ...value, is_new: Boolean(value.is_new) });
									}
								} else {
									formikDialog.setFieldValue("empresa_object", null);
								}
							}}
							filterOptions={(options, params) => {
								const filtered = filter(options, params);
								// Suggest the creation of a new value
								if (params.inputValue !== '') {
									filtered.push({
										nombre: params.inputValue,
										is_new: true,
									});
								}
								return filtered;
							}}
							options={empresas.data ? empresas.data.data : []}
							getOptionLabel={(option) => option.nombre || option}
							renderOption={(option) => option.is_new ? `Incluir "${option.nombre}"` : option.nombre}
							freeSolo
							fullWidth
							size="small"
							noOptionsText="Sin opciones"
							renderInput={(params) => (
								<TextField
									label="Empresa"
									variant="outlined"
									fullWidth
									helperText={formikDialog.errors.empresa_object}
									error={Boolean(formikDialog.errors.empresa_object)}
									{...params}
								/>
							)}
						/>
					</Grid>

					{/* CAMPO CLASIFICACIÓN */}
					<Grid item xs={6}>
						<TextField
							name="lugar_trabajo"
							label="Área de trabajo"
							value={formikDialog.values.lugar_trabajo}
							onChange={formikDialog.handleChange}
							variant="outlined"
							fullWidth
							size="small"
							helperText={formikDialog.errors.lugar_trabajo}
							error={Boolean(formikDialog.errors.lugar_trabajo)}
						/>
					</Grid>

					{/* CAMPO CARGO */}
					<Grid item xs={6}>
						<TextField
							name="cargo"
							label="Cargo"
							value={formikDialog.values.cargo}
							onChange={formikDialog.handleChange}
							variant="outlined"
							fullWidth
							size="small"
							helperText={formikDialog.errors.cargo}
							error={Boolean(formikDialog.errors.cargo)}
						/>
					</Grid>

					{/* CAMPO EXPERIENCIA ESPECÍFICA */}
					<Grid item xs={6}>
						<TextField
							name="experiencia_especifica"
							label="Experiencia Específica"
							value={formikDialog.values.experiencia_especifica}
							onChange={formikDialog.handleChange}
							variant="outlined"
							fullWidth
							size="small"
							helperText={formikDialog.errors.experiencia_especifica}
							error={Boolean(formikDialog.errors.experiencia_especifica)}
						/>
					</Grid>

					{/* CAMPO FECHA DE INICIO */}
					<Grid item xs={4}>
						<DatePicker
							label="Inicio"
							value={formikDialog.values.fecha_inicio}
							onChange={date => formikDialog.setFieldValue("fecha_inicio", date)}
							openTo="year"
							views={["year", "month"]}
							format="MMMM[ de ]YYYY"
							minDate={Moment().add(-80, "years").year().toString()}
							maxDate={Moment().add(1, "years").year().toString()}
							variant="inline"
							inputVariant="outlined"
							fullWidth
							autoOk
							size="small"
							helperText={formikDialog.errors.fecha_inicio}
							error={Boolean(formikDialog.errors.fecha_inicio)}
						/>
					</Grid>

					{/* CAMPO FECHA DE TÉRMINO */}
					<Grid item xs={4}>
						<DatePicker
							label="Término"
							value={formikDialog.values.fecha_termino}
							onChange={date => formikDialog.setFieldValue("fecha_termino", date)}
							openTo="year"
							views={["year", "month"]}
							format="MMMM[ de ]YYYY"
							minDate={Moment().add(-80, "years").year().toString()}
							maxDate={Moment().add(1, "years").year().toString()}
							variant="inline"
							inputVariant="outlined"
							fullWidth
							autoOk
							size="small"
							helperText={formikDialog.errors.fecha_termino}
							error={Boolean(formikDialog.errors.fecha_termino)}
						/>
					</Grid>

					{/* CAMPO ESTÄ TRABAJANDO ACTUALMENTE */}
					<Grid item xs={4}>
						<Autocomplete
							name="is_trabajando_object"
							options={[{ label: "Si", valor: true }, { label: "No", valor: false }]}
							getOptionLabel={(option) => option.label}
							value={formikDialog.values.is_trabajando_object}
							onChange={(event, value) => formikDialog.setFieldValue("is_trabajando_object", value)}
							size="small"
							fullWidth
							noOptionsText="Sin opciones"
							renderInput={(params) => (
								<TextField
									label="¿Aún trabaja ahí?"
									variant="outlined"
									helperText={formikDialog.errors.is_trabajando_object}
									error={Boolean(formikDialog.errors.is_trabajando_object)}
									{...params}
								/>
							)}
						/>
					</Grid>

					{/* CAMPO COMENTARIO */}
					<Grid item xs={12}>
						<TextField
							name="comentario"
							label="Descripción de funciones"
							value={formikDialog.values.comentario}
							onChange={formikDialog.handleChange}
							variant="outlined"
							fullWidth
							multiline
							size="small"
							helperText={formikDialog.errors.comentario}
							error={Boolean(formikDialog.errors.comentario)}
						/>
					</Grid>

					{/* BOTONES AGREGAR/CANCELAR */}
					<Grid item xs={12}>
						<Box display="flex" justifyContent="flex-end">
							<Box pr={1}>
								<Button variant="contained" onClick={formikDialog.submitForm} disabled={isDisabled()} color="primary">
									Agregar experiencia
								</Button>
							</Box>
							<Box pl={1}>
								<Button variant="contained" onClick={handleCloseDialog} color="secondary">
									Cerrar
								</Button>
							</Box>
						</Box>
					</Grid>
				</Grid>
			</Paper>
		</Collapse>
	);
}